import { commonApi } from '@common/utils/baseQuery';
import config from '@config';
import { IFavoritesList, IFavoriteSearchItem, toFavoriteSearch, toFavoritesList } from '@features/favorites/favoritesList';

export interface IFavoriteIds {
  events: number[];
  teams?: number[];
  champs?: { id: number, sportId: number }[];
}

export const FAVORITE_TYPE_EVENT = 1;
export const FAVORITE_TYPE_TEAM = 2;
export const FAVORITE_TYPE_CHAMP = 3;

export type IFavoriteType = typeof FAVORITE_TYPE_EVENT | typeof FAVORITE_TYPE_TEAM | typeof FAVORITE_TYPE_CHAMP;

export interface IFavoriteWrite {
  type: IFavoriteType;
  id: number;
  sportId?: number;
}

export const favoritesApi = commonApi.injectEndpoints({
  endpoints: (builder) => ({
    getFavoriteIds: builder.query<IFavoriteIds, void>({
      query: () => `${config.host}/favorites/ids`,
      // @ts-expect-error
      providesTags: [ 'FavoriteIds' ],
    }),
    getFavoritesList: builder.query<IFavoritesList, void>({
      query: () => `${config.host}/favorites/`,
      transformResponse: (response: unknown) => toFavoritesList(response),
      // @ts-expect-error
      providesTags: [ 'FavoritesList' ],
    }),
    searchFavorites: builder.query<IFavoriteSearchItem[], { type: IFavoriteType, q: string }>({
      query: ({ type, q }) => `${config.host}/favorites/search?type=${type}&q=${encodeURIComponent(q)}`,
      transformResponse: (response: unknown) => toFavoriteSearch(response),
    }),
    addFavorite: builder.mutation<void, IFavoriteWrite>({
      query: ({ type, id, sportId }) => ({
        url: `${config.host}/favorites/`,
        method: 'POST',
        body: sportId ? { type, id, sportId } : { type, id },
      }),
      // @ts-expect-error
      invalidatesTags: [ 'FavoritesList' ],
    }),
    removeFavorite: builder.mutation<void, IFavoriteWrite>({
      query: ({ type, id }) => ({
        url: `${config.host}/favorites/${type}/${id}`,
        method: 'DELETE',
      }),
      // @ts-expect-error
      invalidatesTags: [ 'FavoritesList' ],
    }),
    addFavoriteEvent: builder.mutation<void, number>({
      query: (id) => ({
        url: `${config.host}/favorites/`,
        method: 'POST',
        body: { type: FAVORITE_TYPE_EVENT, id },
      }),
      // @ts-expect-error
      invalidatesTags: [ 'FavoritesList' ],
    }),
    removeFavoriteEvent: builder.mutation<void, number>({
      query: (id) => ({
        url: `${config.host}/favorites/${FAVORITE_TYPE_EVENT}/${id}`,
        method: 'DELETE',
      }),
      // @ts-expect-error
      invalidatesTags: [ 'FavoritesList' ],
    }),
    importFavoriteEvents: builder.mutation<void, number[]>({
      query: (events) => ({
        url: `${config.host}/favorites/import`,
        method: 'POST',
        body: { events },
      }),
    }),
  }),
});

export const {
  useGetFavoriteIdsQuery,
  useGetFavoritesListQuery,
  useLazySearchFavoritesQuery,
  useAddFavoriteMutation,
  useRemoveFavoriteMutation,
  useLazyGetFavoriteIdsQuery,
  useAddFavoriteEventMutation,
  useRemoveFavoriteEventMutation,
  useImportFavoriteEventsMutation,
} = favoritesApi;
